/**
 * Inteligência da Visão Executiva — camada de leitura no front.
 *
 * Interpreta os MESMOS dados que a tela já recebe (/dashboards/transport/{cte,
 * financial,overview}); não faz consulta nova nem altera cálculo, motor ou rota.
 * As regras e limiares são os que a ExecutivePage já usava em "Alertas do
 * período" e "Insights do período" — o que muda é a forma de apresentar: cada
 * ponto de atenção responde às 5 perguntas (o que aconteceu, por que merece
 * atenção, impacto, o que fazer, prioridade) e liga à memória de cálculo.
 *
 * Única diferença deliberada: a concentração de clientes usa o faturamento
 * TOTAL do período (summary.totalValue), não a soma do Top 10 — mesma correção
 * do PR #135.
 */

import type { TransportCteDashboardDto, TransportFinancialDto, TransportOverviewDto } from "@bi/types";
import { fmtBRL, fmtNumber, fmtPct } from "../format";

export type IntelSeverity = "critical" | "warning" | "info";
export type IntelTrend = "up" | "down" | "flat";

export interface IntelEvidence {
  label: string;
  value: string;
  /** Período do número, quando não é o período filtrado (ex.: o de comparação). */
  note?: string;
}

/** Memória de cálculo (/financeiro/memoria/:cardKey ou /operacional/memoria/:cardKey — mesma tela, breadcrumb diferente). */
export interface IntelMemory {
  cardKey: string;
  /** Só para cards que respeitam o período filtrado. */
  periodFrom?: string;
  periodTo?: string;
  /** Prefixo da rota — default "financeiro" (mantém o comportamento atual de quem já usa isso). */
  area?: "financeiro" | "operacional";
}

/** Ponto de atenção: pede uma ação. */
export interface AttentionPoint {
  id: string;
  severity: IntelSeverity;
  /** 1 = mais urgente. */
  priority: number;
  title: string;
  /** O que aconteceu? */
  fact: string;
  /** Por que merece atenção? */
  evidence: IntelEvidence[];
  /** Qual é o impacto? (só diferença medida; nunca causa presumida) */
  impact: string;
  /** O que pode ser feito? */
  action: string;
  memory: IntelMemory | null;
}

/** Leitura do período: contexto, sem desvio que peça ação. */
export interface PeriodReading {
  id: string;
  title: string;
  body: string;
  metric: string;
  trend: IntelTrend;
  memory: IntelMemory | null;
}

export interface ExecutiveIntel {
  attention: AttentionPoint[];
  readings: PeriodReading[];
}

export interface ExecutiveIntelInput {
  cte: Pick<TransportCteDashboardDto, "summary" | "prevSummary" | "comparisonLabel"> | null | undefined;
  financial: Pick<TransportFinancialDto, "arOverdueTotal" | "arOpenTotal" | "cashBalance"> | null | undefined;
  overview: Pick<TransportOverviewDto, "topCustomers"> | null | undefined;
  /** ISO do período filtrado. */
  from: string;
  to: string;
}

/** Variação % de `cur` sobre `prev` (0..100); null sem base. */
export function pctChange(cur: number, prev: number): number | null {
  if (prev === 0) return null;
  return ((cur - prev) / Math.abs(prev)) * 100;
}

const SEVERITY_ORDER: Record<IntelSeverity, number> = { critical: 0, warning: 1, info: 2 };
const absPct = (v: number) => fmtPct(Math.abs(v));

export function buildExecutiveIntel(input: ExecutiveIntelInput): ExecutiveIntel {
  const { cte, financial: fin, overview, from, to } = input;
  const s = cte?.summary;
  const p = cte?.prevSummary;
  const comp = cte?.comparisonLabel?.replace(/^vs\.\s*/i, "") || "período anterior";
  // "setembro de 2026, mesmo período" → "do mesmo período de setembro de 2026"
  const samePeriod = comp.match(/^(.*?),? (?:— )?mesmo período$/);
  const compPhrase = samePeriod ? `do mesmo período de ${samePeriod[1]}` : `do ${comp}`;
  const revenueMemory: IntelMemory = { cardKey: "cte-receita-mes", periodFrom: from, periodTo: to };

  // `weight` desempata a prioridade dentro da mesma severidade: tamanho do impacto em R$.
  const points: Array<Omit<AttentionPoint, "priority"> & { weight: number }> = [];

  // Queda no faturamento: < -10% (base > R$ 100).
  if (s && p && p.totalValue > 100) {
    const r = pctChange(s.totalValue, p.totalValue);
    if (r !== null && r < -10) {
      const lost = p.totalValue - s.totalValue;
      points.push({
        id: "queda-faturamento", severity: "warning", weight: lost,
        title: "Queda no faturamento",
        fact: `O faturamento de CT-e caiu ${absPct(r)} em relação ao período de comparação.`,
        evidence: [
          { label: "Faturamento no período", value: fmtBRL(s.totalValue) },
          { label: "Faturamento no período de comparação", value: fmtBRL(p.totalValue), note: comp },
        ],
        impact: `${fmtBRL(lost)} a menos de receita que no período de comparação.`,
        action: "Verificar carteira de tomadores e volume de operações.",
        memory: revenueMemory,
      });
    }
  }

  // Queda no volume de CT-es: < -15%.
  if (s && p && p.count > 0) {
    const r = pctChange(s.count, p.count);
    if (r !== null && r < -15) {
      points.push({
        // Sem valor em R$ medido: depois dos pontos com impacto financeiro de mesma severidade.
        id: "queda-volume-cte", severity: "warning", weight: 0,
        title: "Queda no volume de CT-es",
        fact: `Foram emitidos ${fmtNumber(s.count)} CT-es, ${absPct(r)} a menos que no período de comparação.`,
        evidence: [
          { label: "CT-es no período", value: fmtNumber(s.count) },
          { label: "CT-es no período de comparação", value: fmtNumber(p.count), note: comp },
        ],
        impact: `${fmtNumber(p.count - s.count)} CT-es a menos no período.`,
        action: "Verificar emissões pendentes e operações em andamento.",
        memory: null,
      });
    }
  }

  // Queda no ticket médio: < -8% (base > R$ 100).
  if (s && p && p.avgTicket > 100) {
    const r = pctChange(s.avgTicket, p.avgTicket);
    if (r !== null && r < -8) {
      const lostPerCte = p.avgTicket - s.avgTicket;
      points.push({
        id: "queda-ticket", severity: "info", weight: lostPerCte * s.count,
        title: "Queda no ticket médio",
        fact: `O ticket médio por CT-e caiu ${absPct(r)} em relação ao período de comparação.`,
        evidence: [
          { label: "Ticket médio no período", value: fmtBRL(s.avgTicket) },
          { label: "Ticket médio no período de comparação", value: fmtBRL(p.avgTicket), note: comp },
        ],
        impact: `${fmtBRL(lostPerCte)} a menos por CT-e. Nos ${fmtNumber(s.count)} CT-es do período, ${fmtBRL(lostPerCte * s.count)}.`,
        action: "Analisar mix de cargas e distâncias médias.",
        memory: null,
      });
    }
  }

  // Inadimplência elevada: > R$ 50 mil (crítica > R$ 200 mil).
  if (fin?.arOverdueTotal && fin.arOverdueTotal > 50_000) {
    const share = fin.arOpenTotal > 0 ? (fin.arOverdueTotal / fin.arOpenTotal) * 100 : null;
    points.push({
      id: "inadimplencia", severity: fin.arOverdueTotal > 200_000 ? "critical" : "warning", weight: fin.arOverdueTotal,
      title: "Inadimplência elevada",
      fact: `Há ${fmtBRL(fin.arOverdueTotal)} em contas a receber vencidas e não pagas.`,
      evidence: [
        { label: "A receber vencido", value: fmtBRL(fin.arOverdueTotal), note: "posição atual" },
        ...(fin.arOpenTotal > 0 ? [{ label: "A receber em aberto (total)", value: fmtBRL(fin.arOpenTotal), note: "posição atual" }] : []),
      ],
      impact: share != null
        ? `${fmtPct(share)} do que a empresa tem a receber está atrasado.`
        : `${fmtBRL(fin.arOverdueTotal)} de receita já faturada ainda não entrou no caixa.`,
      action: "Acionar cobrança dos maiores inadimplentes listados nesta tela.",
      memory: { cardKey: "inadimplencia-total" },
    });
  }

  // Saldo bancário negativo.
  if (fin?.cashBalance !== undefined && fin.cashBalance < 0) {
    points.push({
      id: "saldo-negativo", severity: "critical", weight: Math.abs(fin.cashBalance),
      title: "Saldo bancário negativo",
      fact: `O saldo somado das contas bancárias está negativo em ${fmtBRL(Math.abs(fin.cashBalance))}.`,
      evidence: [{ label: "Saldo bancário", value: fmtBRL(fin.cashBalance), note: "posição atual" }],
      impact: `Faltam ${fmtBRL(Math.abs(fin.cashBalance))} para o saldo voltar a zero.`,
      action: "Avaliar antecipação de recebíveis ou linhas de crédito.",
      memory: { cardKey: "saldo-bancario" },
    });
  }

  // Concentração: maior tomador > 35% do faturamento TOTAL do período.
  const tops = overview?.topCustomers ?? [];
  const totalRevenue = s && s.totalValue > 0 ? s.totalValue : tops.reduce((a, c) => a + c.totalValue, 0);
  const top1 = tops[0];
  if (top1 && totalRevenue > 0) {
    const share = (top1.totalValue / totalRevenue) * 100;
    if (share > 35) {
      points.push({
        id: "concentracao", severity: "info", weight: top1.totalValue,
        title: "Concentração de faturamento",
        fact: `"${top1.name}" responde por ${fmtPct(share)} do faturamento do período.`,
        evidence: [
          { label: `Faturamento de ${top1.name}`, value: fmtBRL(top1.totalValue) },
          { label: "Faturamento total do período", value: fmtBRL(totalRevenue) },
        ],
        impact: `${fmtBRL(top1.totalValue)} do faturamento depende de um único tomador.`,
        action: "Diversificar carteira para reduzir dependência de cliente único.",
        memory: revenueMemory,
      });
    }
  }

  // ── Leituras do período ──────────────────────────────────────────────────────
  const readings: PeriodReading[] = [];

  if (s && p && p.totalValue > 100) {
    const r = pctChange(s.totalValue, p.totalValue);
    if (r !== null) {
      readings.push({
        id: "leitura-faturamento",
        title: `Faturamento ${r > 0 ? "em alta" : "em queda"}`,
        body: `Receita CT-e de ${fmtBRL(s.totalValue)}, ${absPct(r)} ${r > 0 ? "acima" : "abaixo"} ${compPhrase}.`,
        metric: fmtBRL(s.totalValue),
        trend: r > 1 ? "up" : r < -1 ? "down" : "flat",
        memory: revenueMemory,
      });
    }
  }

  if (s && s.count > 0) {
    readings.push({
      id: "leitura-volume",
      title: "Volume de CT-es",
      body: `${fmtNumber(s.count)} CT-es autorizados no período, com ticket médio de ${fmtBRL(s.avgTicket)}.`,
      metric: fmtNumber(s.count),
      trend: "flat",
      memory: null,
    });
  }

  if (tops.length >= 3 && totalRevenue > 0) {
    const pct3 = (tops.slice(0, 3).reduce((a, c) => a + c.totalValue, 0) / totalRevenue) * 100;
    const leader = tops[0];
    readings.push({
      id: "leitura-concentracao",
      title: "Concentração por tomador",
      body: `Os 3 maiores tomadores respondem por ${fmtPct(pct3)} do faturamento.${leader ? ` Líder: "${leader.name}" (${leader.formatted}).` : ""}`,
      metric: fmtPct(pct3),
      trend: pct3 > 60 ? "down" : "flat",
      memory: revenueMemory,
    });
  }

  if (fin?.arOpenTotal && fin.arOpenTotal > 0) {
    const overduePct = (fin.arOverdueTotal / fin.arOpenTotal) * 100;
    readings.push({
      id: "leitura-recebiveis",
      title: "Posição de recebíveis",
      body: `Total a receber em aberto: ${fmtBRL(fin.arOpenTotal)}.${overduePct > 0.5 ? ` Vencido: ${fmtPct(overduePct)} (${fmtBRL(fin.arOverdueTotal)}).` : " Sem inadimplência identificada."}`,
      metric: fmtBRL(fin.arOpenTotal),
      trend: overduePct > 20 ? "down" : overduePct > 0 ? "flat" : "up",
      memory: { cardKey: "a-receber-em-aberto" },
    });
  }

  if (s && p && p.avgTicket > 100) {
    const r = pctChange(s.avgTicket, p.avgTicket);
    if (r !== null && Math.abs(r) > 2) {
      readings.push({
        id: "leitura-ticket",
        title: `Ticket médio ${r > 0 ? "em alta" : "em queda"}`,
        body: `Ticket médio atual de ${fmtBRL(s.avgTicket)}, ${absPct(r)} ${r > 0 ? "acima" : "abaixo"} do período anterior (${fmtBRL(p.avgTicket)}).`,
        metric: fmtBRL(s.avgTicket),
        trend: r > 1 ? "up" : r < -1 ? "down" : "flat",
        memory: null,
      });
    }
  }

  // Prioridade: críticos primeiro; na mesma severidade, o de maior impacto em R$.
  points.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] || b.weight - a.weight);
  const attention = points.map(({ weight: _w, ...pt }, i) => ({ ...pt, priority: i + 1 }));

  return { attention, readings };
}

/** Movimento de um indicador no resumo executivo ("O que mudou"). */
export interface SummaryMove {
  label: string;
  /** "+3,2%" / "-9,1%"; null sem base de comparação. */
  change: string | null;
  /** Se o movimento é bom para a empresa. */
  good: boolean | null;
}

export interface ExecutiveSummaryData {
  /** "Como estamos?" */
  status: "critical" | "warning" | "ok";
  headline: string;
  /** "O que mudou?" */
  moves: SummaryMove[];
  /** "O que priorizar?" — até 3 pontos de atenção, já em ordem de prioridade. */
  priorities: AttentionPoint[];
}

/** Resumo executivo determinístico: só reorganiza números já exibidos na tela. */
export function buildExecutiveSummary(
  intel: ExecutiveIntel,
  cte: ExecutiveIntelInput["cte"],
): ExecutiveSummaryData {
  const critical = intel.attention.filter((a) => a.severity === "critical").length;
  const warning = intel.attention.filter((a) => a.severity === "warning").length;

  const status: ExecutiveSummaryData["status"] = critical > 0 ? "critical" : warning > 0 ? "warning" : "ok";
  const parts: string[] = [];
  if (critical > 0) parts.push(`${critical} ${critical === 1 ? "ponto crítico" : "pontos críticos"}`);
  if (warning > 0) parts.push(`${warning} ${warning === 1 ? "ponto de atenção" : "pontos de atenção"}`);
  const headline = parts.length > 0
    ? `${parts.join(" e ")} no período.`
    : "Nenhum ponto crítico ou de atenção no período.";

  const s = cte?.summary;
  const p = cte?.prevSummary;
  const move = (label: string, cur?: number, prev?: number): SummaryMove => {
    const r = cur != null && prev != null ? pctChange(cur, prev) : null;
    return {
      label,
      change: r == null ? null : `${r > 0 ? "+" : r < 0 ? "-" : ""}${absPct(r)}`,
      good: r == null || Math.abs(r) < 0.05 ? null : r > 0,
    };
  };

  return {
    status,
    headline,
    moves: [
      move("Faturamento", s?.totalValue, p?.totalValue),
      move("CT-es emitidos", s?.count, p?.count),
      move("Ticket médio", s?.avgTicket, p?.avgTicket),
    ],
    priorities: intel.attention.slice(0, 3),
  };
}
